<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <AssetFilters title="Entités matérielles" asset-label="Entités matérielles" :active-filter="viewFilter"
      :stats="stats" :is-loading="isLoading" @update:active-filter="setFilter" @refresh="refreshAssets" />

    <MaterialEntitySubset :items="filteredAssets as MaterialEntityWithMemos[]" :show-toolbar="true" :show-actions="true"
      :show-edit-button="true" :show-delete-button="true" :show-duplicate-button="true" @create="openCreationModal"
      @edit="editDataset" @delete="handleDeleteDataset" @duplicate="duplicateDataset" />

    <!-- Composant MaterialEntityCreator -->
    <MaterialEntityCreator v-model:show="showCreatorModal" :user-id="userId" :mode="creatorMode"
      :initial-data="selectedEntity" @material-entity-created="handleEntityCreated"
      @material-entity-updated="handleEntityUpdated" />
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { useMaterialEntities } from '@/composables/useAssets';
import { useAuthStore } from '@/stores/auth';
import type { NotificationType } from "@/types/notification";
import { computed, ref } from "vue";
import type { MaterialEntityWithMemos } from "../../api/types/entities/materialEntity/types";
import Breadcrumbs from "../../components/Breadcrumbs.vue";
import Notification from "../../components/Notification.vue";
import AssetFilters from "../../components/UI/AssetFilters.vue";
import MaterialEntityCreator from "../../components/creators/MaterialEntityCreator.vue";
import MaterialEntitySubset from "../../components/subsets/MaterialEntitySubset.vue";
import { useBreadcrumbsStore } from "../../stores/breadcrumbs";

const authStore = useAuthStore();
const userId = computed(() => authStore.user?.uuid || '');
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showCreatorModal = ref(false);
const creatorMode = ref<'create' | 'edit' | 'duplicate'>('create');
const selectedEntity = ref<MaterialEntityWithMemos | undefined>(undefined);
const breadcrumbsStore = useBreadcrumbsStore();

// Utilisation du nouveau système de cache
const {
  filteredAssets,
  stats,
  isLoading,
  viewFilter,
  setFilter,
  refreshAssets,
  addAsset,
  updateAsset,
  removeAsset
} = useMaterialEntities({ autoLoad: true });

const crumbs = [
  { name: 'Outils', path: '/dashboard' },
  { name: 'Entités matérielles', path: '/mymaterialentities' }
];

breadcrumbsStore.updateBreadcrumbs(crumbs);

const editDataset = (entity: MaterialEntityWithMemos): void => {
  // Vérifier si l'utilisateur peut éditer cette entité (pas d'édition sur entités publiques d'autres utilisateurs)
  if (!authStore.canEditAsset(entity as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas éditer cette entité matérielle", "error");
    return;
  }

  selectedEntity.value = entity;
  creatorMode.value = 'edit';
  showCreatorModal.value = true;
};

const openCreationModal = (): void => {
  selectedEntity.value = undefined;
  creatorMode.value = 'create';
  showCreatorModal.value = true;
};

const duplicateDataset = (entity: MaterialEntityWithMemos): void => {
  selectedEntity.value = {
    ...entity,
    uuid: entity.uuid
  };
  creatorMode.value = 'duplicate';
  showCreatorModal.value = true;
};

const handleEntityCreated = (entity: MaterialEntityWithMemos): void => {
  addAsset(entity);
  showNotification("Entité matérielle créée avec succès", "success");
};

const handleEntityUpdated = (entity: MaterialEntityWithMemos): void => {
  updateAsset(entity);
  showNotification("Entité matérielle mise à jour avec succès", "success");
};

const handleDeleteDataset = async (entity: MaterialEntityWithMemos): Promise<void> => {
  // Vérifier si l'utilisateur peut supprimer cette entité (pas de suppression sur entités publiques d'autres utilisateurs)
  if (!authStore.canDeleteAsset(entity as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas supprimer cette entité matérielle", "error");
    return;
  }

  console.log('🗑️ [MyMaterialEntities] Tentative de suppression de l\'entité:', entity.uuid);
  
  try {
    // Forcer un refresh du cache avant la suppression
    await refreshAssets();
    
    const { materialEntities } = await import("../../api/materialEntities");
    await materialEntities.deleteMaterialEntity(entity.uuid);
    
    // Supprimer du cache local
    removeAsset(entity.uuid);
    
    showNotification("Entité matérielle supprimée avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression de l'entité matérielle:", error);
    
    // Si c'est une erreur 404, essayer de rafraîchir la liste
    if ((error as any)?.status === 404) {
      console.log('🔄 [MyMaterialEntities] Entité non trouvée, rafraîchissement de la liste...');
      await refreshAssets();
      showNotification("Liste mise à jour - l'entité a peut-être déjà été supprimée", "warning");
    } else {
      showNotification("Erreur lors de la suppression de l'entité matérielle", "error");
    }
  }
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};


</script>

<style>
.input-edit {
  margin-top: 0.25rem;
  display: block;
  width: 100%;
  padding: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.input-edit:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
}
</style>